import React, { useState, useEffect } from "react";

const InstallAppButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [hover, setHover] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | installed | dismissed

  useEffect(() => {
    const isInstalled =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone ||
      document.referrer.includes("android-app://");

    if (isInstalled) {
      setStatus("installed");
      return;
    }

    // ✅ Pick up already-captured prompt
    if (window.__deferredInstallPrompt) {
      setDeferredPrompt(window.__deferredInstallPrompt);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      window.__deferredInstallPrompt = e;
    };

    // ✅ Named so it can be cleaned up
    const onInstalled = () => {
      setStatus("installed");
      setDeferredPrompt(null);
      window.__deferredInstallPrompt = null;
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);

    // ✅ Clean up both
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      alert(
        "To install:\niPhone: Share → Add to Home Screen\nAndroid: Menu → Install App",
      );
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);

    if (outcome === "accepted") {
      setStatus("installed");
    } else {
      setStatus("dismissed");
      // reset back to idle after 3s so they can try again
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  // ✅ SHOW "ALREADY INSTALLED" STATE
  if (status === "installed") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          padding: "0.85rem 1.25rem",
          width: "100%",
          boxSizing: "border-box",
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          border: "none",
          borderRadius: "14px",
          color: "#fff",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <div style={{ textAlign: "left", lineHeight: 1.25 }}>
          <div
            style={{
              fontSize: "0.6rem",
              color: "rgba(255,255,255,0.85)",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            App Installed
          </div>
          <div style={{ fontSize: "0.92rem", fontWeight: "800" }}>
            Ready to Use
          </div>
        </div>
      </div>
    );
  }

  // ── Dismissed state ──
  if (status === "dismissed") {
    return (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.65rem",
          padding: "0.85rem 1.25rem",
          background: "rgba(102,126,234,0.05)",
          border: "1.5px solid #ede9fe",
          borderRadius: "14px",
          width: "100%",
        }}
      >
        <div
          style={{
            fontSize: "0.85rem",
            color: "#9ca3af",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          No worries — you can install anytime from your browser menu.
        </div>
      </div>
    );
  }

  // ── Default install button ──
  return (
    <button
      onClick={handleInstall}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.85rem 1.25rem",
        width: "100%",
        boxSizing: "border-box",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        border: "none",
        borderRadius: "14px",
        color: "#fff",
        fontFamily: "'DM Sans', sans-serif",
        fontWeight: "700",
        fontSize: "0.95rem",
        cursor: "pointer",
        boxShadow: hover
          ? "0 14px 36px rgba(118,75,162,0.38)"
          : "0 6px 20px rgba(102,126,234,0.28)",
        transform: hover ? "translateY(-2px)" : "translateY(0)",
        transition: "transform 0.2s, box-shadow 0.2s",
      }}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12" y2="18.01" />
        <path d="M9 10l3 3 3-3" />
        <line x1="12" y1="7" x2="12" y2="13" />
      </svg>
      <div style={{ textAlign: "left", lineHeight: 1.25 }}>
        <div
          style={{
            fontSize: "0.6rem",
            color: "rgba(255,255,255,0.72)",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          Install for free
        </div>
        <div style={{ fontSize: "0.92rem", fontWeight: "800" }}>
          Download App
        </div>
      </div>
    </button>
  );
};

export default InstallAppButton;
